import React, { createContext, useCallback, useContext, useState } from "react";
import { Program, ProgramWeek, Module, ProgramWeekStatus } from "../types/ProgramModel";
import * as programService from "../services/programService";

interface ProgramContextType {
  programs: Program[];
  setPrograms: (programs: Program[]) => void;
  fetchPrograms: () => Promise<void>;
  fetchProgramWeek: (programId: string, week: ProgramWeek) => Promise<void>;
  fetchProgramModule: (
    module: Module,
    programId: string,
    weekNumber: number
  ) => Promise<void>;
  fetchModuleQuestions: (
    moduleId: string,
    programId: string,
    weekNumber: number
  ) => Promise<void>;
}

const ProgramContext = createContext<ProgramContextType | undefined>(undefined);

export const useProgram = () => {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error("useProgram must be used within a ProgramProvider");
  }
  return context;
};

export const ProgramProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [programs, setPrograms] = useState<Program[]>([]);

  const generateWeeksInProgram = (inputPrograms: Program[]): Program[] => {
    return inputPrograms.map((program) => ({
      ...program,
      weeks: Array.from({ length: program.weekCount }, (_, index) => ({
        weekId: `${program.id}_week${index + 1}`,
        weekNumber: index + 1,
        name: `Week ${index + 1}`,
        modules: [],
        status: ProgramWeekStatus.Incomplete,
        progress: 0,
      })),
    }));
  };

  const fetchPrograms = useCallback(async () => {
    try {
      const response = await programService.getPrograms();
      const fetchedPrograms = response.data;
      setPrograms(generateWeeksInProgram(fetchedPrograms));
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  }, []);

  const fetchProgramWeek = useCallback(
    async (programId: string, week: ProgramWeek) => {
      try {
        const response = await programService.getProgramWeek(week.weekId);
        const weekResponse = response.data;

        setPrograms((prevPrograms) =>
          prevPrograms.map((p: Program) => {
            if (p.id === programId) {
              return {
                ...p,
                weeks: p.weeks?.map((w) =>
                  w.weekNumber === week.weekNumber
                    ? { ...w, modules: weekResponse.modules }
                    : w
                ),
              };
            }
            return p;
          })
        );
      } catch (error) {
        console.error("Error fetching program week:", error);
      }
    },
    []
  ); // Dependency array is empty if no external variables are used

  const fetchProgramModule = useCallback(
    async (module: Module, programId: string, weekNumber: number) => {
      try {
        const response = await programService.getProgramModule(module.moduleId);
        const updatedModule = response.data;

        setPrograms((prevPrograms) =>
          prevPrograms.map((program) => {
            if (program.id === programId) {
              return {
                ...program,
                weeks: program.weeks?.map((week) => {
                  if (week.weekNumber === weekNumber) {
                    return {
                      ...week,
                      modules: week.modules.map((m) =>
                        m.moduleId === module.moduleId
                          ? { ...m, ...updatedModule }
                          : m
                      ),
                    };
                  }
                  return week;
                }),
              };
            }
            return program;
          })
        );
      } catch (error) {
        console.error("Error fetching module data:", error);
      }
    },
    []
  );

  const fetchModuleQuestions = useCallback(
    async (moduleId: string, programId: string, weekNumber: number) => {
      try {
        const response = await programService.getQuestions(moduleId);
        const questions = response.data;

        setPrograms((prevPrograms) =>
          prevPrograms.map((program) => {
            if (program.id === programId) {
              return {
                ...program,
                weeks: program.weeks?.map((week) => {
                  if (week.weekNumber === weekNumber) {
                    return {
                      ...week,
                      modules: week.modules.map((module) =>
                        module.moduleId === moduleId
                          ? { ...module, questions }
                          : module
                      ),
                    };
                  }
                  return week;
                }),
              };
            }
            return program;
          })
        );
      } catch (error) {
        console.error("Error fetching module questions:", error);
      }
    },
    []
  );

  return (
    <ProgramContext.Provider
      value={{
        programs,
        setPrograms,
        fetchPrograms,
        fetchProgramWeek,
        fetchProgramModule,
        fetchModuleQuestions,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
};
